// Alpine-Komponente für den Marker-Vergleich-Teaser auf Produktseiten.
// Fall A (keine gespeicherte Auswahl) ist bereits serverseitig gerendert und
// funktioniert ohne JavaScript. Diese Komponente ersetzt NUR den dynamischen
// Bereich, wenn eine gültige Sammlung gefunden wird (Fälle B/C/D) – die Mathematik
// dafür kommt komplett aus der bestehenden, framework-freien Vergleichslogik
// (keine eigene Neuberechnung im Widget).

import { buildCollection, compareToTarget, rankSets, interpolate, formatPlural, buildSelectionUrl } from './lgt-compare-logic.js';
import { readStoredSelection } from './lgt-selection-storage.js';

// Texte aus Block-Settings nutzen einfache Klammern ({set}, {best_new}) statt {{ }},
// weil Shopifys Theme-Editor {{ }} in Text-Settings als "Dynamic Source"-Platzhalter
// interpretiert und unbekannte Tokens vor dem Rendern stillschweigend leert.
function interpolateSetting(template, vars) {
  return interpolate(template.replace(/\{(\w+)\}/g, '{{ $1 }}'), vars);
}

document.addEventListener('alpine:init', () => {
  Alpine.data('lgtCompareTeaser', (config) => ({
    currentSetHandle: config.currentSetHandle,
    compareUrl: config.compareUrl,
    fewNewThreshold: config.fewNewThreshold || 3,
    sets: config.sets || [],
    strings: config.strings || {},

    state: 'A',
    ownedHandles: [],
    newColors: [],
    afterTotal: 0,
    bestSet: null,
    bestNewCount: 0,
    setOwned: false,

    init() {
      const validHandles = this.sets.map((s) => s.handle);
      const selection = readStoredSelection(validHandles);
      this.ownedHandles = selection.owned;

      // Fall A: keine gespeicherte Sammlung und kein Vergleichsset -> SSR-Zustand bleibt stehen.
      if (this.ownedHandles.length === 0 && !selection.compare) {
        this.emitView('A');
        return;
      }

      const currentSet = this.sets.find((s) => s.handle === this.currentSetHandle);
      if (!currentSet) {
        this.emitView('A');
        return;
      }

      const ownedSets = this.sets.filter((s) => this.ownedHandles.includes(s.handle));
      const collection = buildCollection(ownedSets);
      const result = compareToTarget(collection, currentSet);
      this.setOwned = this.ownedHandles.includes(this.currentSetHandle);

      const candidates = this.sets.filter(
        (s) => !this.ownedHandles.includes(s.handle) && s.handle !== this.currentSetHandle
      );
      const ranking = rankSets(collection, candidates);
      const best = ranking.find((entry) => entry.newCount > 0) || null;
      this.bestSet = best ? best.set : null;
      this.bestNewCount = best ? best.newCount : 0;

      this.newColors = result.newColors;
      this.afterTotal = result.collectionSizeAfter;

      if (this.setOwned || result.newColors.length === 0) {
        this.state = 'D';
      } else if (result.newColors.length <= this.fewNewThreshold) {
        this.state = 'C';
      } else {
        this.state = 'B';
      }

      this.emitView(this.state);
    },

    emitView(state) {
      document.dispatchEvent(
        new CustomEvent('lgt:teaser_view', { detail: { state, set: this.currentSetHandle } })
      );
    },

    emitClick(target) {
      document.dispatchEvent(
        new CustomEvent('lgt:teaser_click', { detail: { target, set: this.currentSetHandle } })
      );
    },

    get resultLink() {
      return buildSelectionUrl(this.compareUrl, { owned: this.ownedHandles, compare: this.currentSetHandle });
    },

    get bestSetLink() {
      if (!this.bestSet) return this.compareUrl;
      return buildSelectionUrl(this.compareUrl, { owned: this.ownedHandles, compare: this.bestSet.handle });
    },

    get resultNewColorsText() {
      return formatPlural(this.strings.resultNewColors, this.newColors.length);
    },

    get afterTotalText() {
      return formatPlural(this.strings.afterTotal, this.afterTotal, { total: this.afterTotal });
    },

    get ownedText() {
      return this.setOwned ? this.strings.alreadyHaveText : this.strings.allOwnedText;
    },

    get bestSetHintFewNewText() {
      if (!this.bestSet) return '';
      return interpolateSetting(this.strings.bestSetHintFewNew, { set: this.bestSet.title, best_new: this.bestNewCount });
    },

    get bestSetHintZeroNewText() {
      if (!this.bestSet) return '';
      return interpolateSetting(this.strings.bestSetHintZeroNew, { set: this.bestSet.title, best_new: this.bestNewCount });
    },
  }));
});
